import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Check, CircleDollarSign, Gauge, Layers3, Settings2, ShieldCheck, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { BASE_PLAN_CURRENCY, PLAN_CURRENCY_OPTIONS, convertPlanPriceFromBase, convertPlanPriceToBase, formatPlanPriceFromBase } from '../lib/currency';
import { AI_DASHBOARD_ADDON_DEFINITIONS, SUITE_ADDON_DEFINITIONS, ALL_ADDON_KEYS, getAiDashboardAddonPricing, getAiDashboardAddonTotals } from '../lib/aiDashboardAddons';
import { workspaceAdminAPI } from '../services/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/utils';
import { CardSkeleton } from '../components/common/States';

const buildEmptyRolePermissions = (roles = []) =>
  (roles || []).reduce((acc, role) => ({ ...acc, [role]: [] }), {});

const buildDefaultUsageLimits = () => ({
  tenants: 1,
  sub_tenants: 0,
  users_per_tenant: 10,
  max_users: 10,
  max_employees: 10,
  storage_limit_gb: 5,
  enable_trial: true,
  trial_days: 14,
  billing_model: 'self',
  ai_integration: true,
  is_default_plan: false,
  addons: [],
  addon_pricing: {},
  is_free: false,
});

const SuperadminPlanEditPage = ({ planId, mode = 'edit', onBack }) => {
  const isCreateMode = mode === 'create';
  const [catalog, setCatalog] = useState({ modules: [], permissions: [], roles: [] });
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState(BASE_PLAN_CURRENCY);
  const [form, setForm] = useState({
    code: '',
    name: '',
    description: '',
    monthly_price: '0',
    yearly_price: '0',
    is_active: true,
    module_access: [],
    role_permissions: {},
    usage_limits: buildDefaultUsageLimits(),
  });

  const selectedPlan = useMemo(
    () => plans.find((row) => String(row.id) === String(planId)),
    [plans, planId]
  );
  const aiAddonKeys = useMemo(
    () => AI_DASHBOARD_ADDON_DEFINITIONS.map((addon) => addon.key),
    []
  );
  const customAddonValue = useMemo(
    () => (form.usage_limits?.addons || []).filter((addon) => !ALL_ADDON_KEYS.includes(addon)).join(', '),
    [form.usage_limits?.addons]
  );

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [catalogRes, plansRes] = await Promise.all([
        workspaceAdminAPI.getSubscriptionCatalog(),
        isCreateMode ? Promise.resolve({ data: [] }) : workspaceAdminAPI.listSubscriptionPlans({ include_inactive: true }),
      ]);
      const nextCatalog = catalogRes.data || { modules: [], permissions: [], roles: [] };
      const nextPlans = Array.isArray(plansRes.data) ? plansRes.data : [];
      setCatalog(nextCatalog);
      setPlans(nextPlans);
      if (isCreateMode) {
        setForm({
          code: '',
          name: '',
          description: '',
          monthly_price: '0',
          yearly_price: '0',
          is_active: true,
          module_access: [],
          role_permissions: buildEmptyRolePermissions(nextCatalog.roles),
          usage_limits: buildDefaultUsageLimits(),
        });
        return;
      }
      const plan = nextPlans.find((row) => String(row.id) === String(planId));
      if (!plan) {
        toast.error('Plan not found');
        onBack?.();
        return;
      }
      setForm({
        code: plan.code || '',
        name: plan.name || '',
        description: plan.description || '',
        monthly_price: String(plan.monthly_price || 0),
        yearly_price: String(plan.yearly_price || 0),
        is_active: Boolean(plan.is_active),
        module_access: plan.module_access || [],
        role_permissions: plan.role_permissions || buildEmptyRolePermissions(nextCatalog.roles),
        usage_limits: {
          ...buildDefaultUsageLimits(),
          ...plan.usage_limits,
          tenants: plan.usage_limits?.tenants ?? 1,
          sub_tenants: plan.usage_limits?.sub_tenants ?? 0,
          users_per_tenant: plan.usage_limits?.users_per_tenant ?? 10,
          max_users: plan.usage_limits?.max_users ?? plan.usage_limits?.users_per_tenant ?? 10,
          max_employees: plan.usage_limits?.max_employees ?? plan.usage_limits?.employee_limit ?? 10,
          storage_limit_gb: plan.usage_limits?.storage_limit_gb ?? 5,
          enable_trial: Boolean(plan.usage_limits?.enable_trial ?? ((plan.usage_limits?.trial_days ?? 0) > 0)),
          trial_days: plan.usage_limits?.trial_days ?? 0,
          billing_model: plan.usage_limits?.billing_model ?? 'self',
          ai_integration: Boolean(plan.usage_limits?.ai_integration ?? true),
          is_default_plan: Boolean(plan.usage_limits?.is_default_plan),
          addons: Array.isArray(plan.usage_limits?.addons) ? plan.usage_limits.addons : [],
          addon_pricing: plan.usage_limits?.addon_pricing || {},
          is_free: Boolean(plan.usage_limits?.is_free),
        },
      });
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load plan details'));
    } finally {
      setLoading(false);
    }
  }, [isCreateMode, planId, onBack]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const toggleModule = (moduleKey, checked) => {
    setForm((prev) => ({
      ...prev,
      module_access: checked
        ? [...prev.module_access, moduleKey]
        : prev.module_access.filter((row) => row !== moduleKey),
    }));
  };

  const togglePermission = (role, permission, checked) => {
    setForm((prev) => {
      const existing = Array.isArray(prev.role_permissions?.[role]) ? prev.role_permissions[role] : [];
      const next = checked ? [...existing, permission] : existing.filter((row) => row !== permission);
      return {
        ...prev,
        role_permissions: {
          ...prev.role_permissions,
          [role]: Array.from(new Set(next)),
        },
      };
    });
  };

  const toggleAddon = (addonKey, checked) => {
    setForm((prev) => {
      const existingAddons = Array.isArray(prev.usage_limits?.addons) ? prev.usage_limits.addons : [];
      const nextAddons = checked
        ? Array.from(new Set([...existingAddons, addonKey]))
        : existingAddons.filter((row) => row !== addonKey);
      const existingAddonPricing = prev.usage_limits?.addon_pricing || {};
      const defaultPricing = getAiDashboardAddonPricing(prev.usage_limits, addonKey);

      return {
        ...prev,
        usage_limits: {
          ...prev.usage_limits,
          addons: nextAddons,
          addon_pricing: {
            ...existingAddonPricing,
            [addonKey]: existingAddonPricing[addonKey] || defaultPricing,
          },
        },
      };
    });
  };

  const updateAddonPricing = (addonKey, field, value) => {
    setForm((prev) => {
      const currentPricing = getAiDashboardAddonPricing(prev.usage_limits, addonKey);
      return {
        ...prev,
        usage_limits: {
          ...prev.usage_limits,
          addon_pricing: {
            ...(prev.usage_limits?.addon_pricing || {}),
            [addonKey]: {
              ...currentPricing,
              [field]: value,
            },
          },
        },
      };
    });
  };

  const savePlan = async () => {
    try {
      setSaving(true);
      const payload = {
        code: form.code,
        name: form.name,
        description: form.description,
        monthly_price: Number(form.monthly_price || 0),
        yearly_price: Number(form.yearly_price || 0),
        is_active: Boolean(form.is_active),
        module_access: form.module_access,
        role_permissions: form.role_permissions,
        usage_limits: form.usage_limits,
      };
      if (isCreateMode) {
        await workspaceAdminAPI.createSubscriptionPlan(payload);
      } else {
        await workspaceAdminAPI.updateSubscriptionPlan(Number(planId), payload);
      }
      toast.success(isCreateMode ? 'Plan created' : 'Plan updated');
      onBack?.();
    } catch (error) {
      toast.error(getErrorMessage(error, isCreateMode ? 'Failed to create plan' : 'Failed to update plan'));
    } finally {
      setSaving(false);
    }
  };

  const isFreePlan = Boolean(form.usage_limits?.is_free);
  const isTrialEnabled = Boolean(form.usage_limits?.enable_trial);
  const isAiIntegrationEnabled = Boolean(form.usage_limits?.ai_integration);
  const isDefaultPlan = Boolean(form.usage_limits?.is_default_plan);
  const includedModulesCount = form.module_access.length;
  const enabledRoleCount = Object.values(form.role_permissions || {}).filter((permissions) => Array.isArray(permissions) && permissions.length > 0).length;
  const trialDays = isTrialEnabled ? Number(form.usage_limits?.trial_days || 0) : 0;
  const storageLimit = Number(form.usage_limits?.storage_limit_gb || 0);
  const maxUsersValue = Number(form.usage_limits?.max_users ?? form.usage_limits?.users_per_tenant ?? 0);
  const maxEmployeesValue = Number(form.usage_limits?.max_employees ?? 0);
  const maxUsersLabel = maxUsersValue === -1 ? 'Unlimited users' : `${maxUsersValue || 0} users`;
  const maxEmployeesLabel = maxEmployeesValue === -1 ? 'Unlimited employees' : `${maxEmployeesValue || 0} employees`;
  const selectedAiAddonCount = AI_DASHBOARD_ADDON_DEFINITIONS.filter((addon) => form.usage_limits?.addons?.includes(addon.key)).length;
  const addonTotals = getAiDashboardAddonTotals(form.usage_limits || {});
  const currentSubscriptionMonthlyPrice = Number(form.monthly_price || 0) + Number(addonTotals.monthly_price || 0);
  const currentSubscriptionYearlyPrice = Number(form.yearly_price || 0) + Number(addonTotals.yearly_price || 0);

  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">{isCreateMode ? 'Create Subscription Plan' : 'Edit Subscription Plan'}</h2>
          <p className="text-sm text-muted-foreground">{isCreateMode ? 'Preparing plan creator...' : 'Loading plan details...'}</p>
        </div>
        <CardSkeleton rows={6} />
        <CardSkeleton rows={4} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">{isCreateMode ? 'Create Subscription Plan' : 'Edit Subscription Plan'}</h2>
          <p className="text-sm text-muted-foreground">
            {isCreateMode ? 'Create a new subscription plan and access policy' : `${selectedPlan?.name || 'Subscription Plan'} (${selectedPlan?.code || '-'})`}
          </p>
        </div>
        <Button variant="outline" onClick={onBack}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Subscription Plans
        </Button>
      </div>

      <Card className="border-primary/10 bg-gradient-to-br from-card via-card to-primary/[0.03]">
        <CardContent className="pt-6">
          <div className="grid gap-3 md:grid-cols-4">
            <div className="rounded-xl border border-border bg-muted/20 px-4 py-3">
              <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Pricing Mode</p>
              <div className="mt-2 flex items-center gap-2">
                <Badge variant={isFreePlan ? 'soft' : 'default'}>{isFreePlan ? 'Startup Plan' : 'Paid Plan'}</Badge>
                <span className="text-xs text-muted-foreground">{selectedCurrency}</span>
              </div>
            </div>
            <div className="rounded-xl border border-border bg-muted/20 px-4 py-3">
              <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Trial Window</p>
              <p className="mt-2 text-lg font-semibold text-foreground">{trialDays > 0 ? `${trialDays} days` : 'No trial'}</p>
            </div>
            <div className="rounded-xl border border-border bg-muted/20 px-4 py-3">
              <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Capacity</p>
              <p className="mt-2 text-lg font-semibold text-foreground">{maxUsersLabel}</p>
              <p className="mt-1 text-xs text-muted-foreground">{maxEmployeesLabel}</p>
            </div>
            <div className="rounded-xl border border-border bg-muted/20 px-4 py-3">
              <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Storage</p>
              <p className="mt-2 text-lg font-semibold text-foreground">{storageLimit > 0 ? `${storageLimit} GB` : 'Custom'}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <span className="grid h-8 w-8 place-items-center rounded-lg bg-primary/10 text-primary">
              <CircleDollarSign className="h-4 w-4" />
            </span>
            Pricing &amp; Identity
          </CardTitle>
          <CardDescription>Plan code, name, and recurring pricing across currencies.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-4 gap-3">
            <div>
              <Label>Code</Label>
              <Input value={form.code} onChange={(e) => setForm((prev) => ({ ...prev, code: e.target.value }))} />
            </div>
            <div>
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))} />
            </div>
            <div>
              <Label>Monthly Price ({selectedCurrency})</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                disabled={isFreePlan}
                value={convertPlanPriceFromBase(form.monthly_price, selectedCurrency)}
                onChange={(e) => setForm((prev) => ({ ...prev, monthly_price: convertPlanPriceToBase(e.target.value, selectedCurrency) }))}
              />
            </div>
            <div>
              <Label>Yearly Price ({selectedCurrency})</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                disabled={isFreePlan}
                value={convertPlanPriceFromBase(form.yearly_price, selectedCurrency)}
                onChange={(e) => setForm((prev) => ({ ...prev, yearly_price: convertPlanPriceToBase(e.target.value, selectedCurrency) }))}
              />
            </div>
          </div>
          <div className="grid md:grid-cols-4 gap-3">
            <div className="md:col-span-1">
              <Label>Status</Label>
              <Select
                value={form.is_active ? 'active' : 'inactive'}
                onValueChange={(value) => setForm((prev) => ({ ...prev, is_active: value === 'active' }))}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="rounded-xl border px-4 py-3 md:col-span-1">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <Label className="text-sm">Startup Plan</Label>
                  <p className="text-xs text-muted-foreground">Keep pricing at zero and mark this plan as startup.</p>
                </div>
                <Switch
                  checked={isFreePlan}
                  onCheckedChange={(checked) =>
                    setForm((prev) => ({
                      ...prev,
                      monthly_price: checked ? '0' : prev.monthly_price,
                      yearly_price: checked ? '0' : prev.yearly_price,
                      usage_limits: {
                        ...prev.usage_limits,
                        is_free: Boolean(checked),
                      },
                    }))
                  }
                />
              </div>
            </div>
            <div className="md:col-span-1">
              <Label>Trial Period (days)</Label>
              <Input
                type="number"
                min="0"
                disabled={!isTrialEnabled}
                value={String(form.usage_limits?.trial_days ?? 0)}
                onChange={(e) => setForm((prev) => ({ ...prev, usage_limits: { ...prev.usage_limits, trial_days: e.target.value } }))}
              />
            </div>
            <div className="md:col-span-1">
              <Label>Currency</Label>
              <Select value={selectedCurrency} onValueChange={setSelectedCurrency}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PLAN_CURRENCY_OPTIONS.map((currency) => (
                    <SelectItem key={currency} value={currency}>{currency}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            <div className="rounded-md border border-primary/15 bg-primary/5 px-3 py-2 text-xs text-muted-foreground">
              <p className="font-medium text-foreground">Current subscription monthly: {formatPlanPriceFromBase(currentSubscriptionMonthlyPrice, selectedCurrency)}</p>
              <p className="mt-1">Base {formatPlanPriceFromBase(form.monthly_price, selectedCurrency)} + AI add-ons {formatPlanPriceFromBase(addonTotals.monthly_price, selectedCurrency)}</p>
            </div>
            <div className="rounded-md border border-primary/15 bg-primary/5 px-3 py-2 text-xs text-muted-foreground">
              <p className="font-medium text-foreground">Current subscription yearly: {formatPlanPriceFromBase(currentSubscriptionYearlyPrice, selectedCurrency)}</p>
              <p className="mt-1">Base {formatPlanPriceFromBase(form.yearly_price, selectedCurrency)} + AI add-ons {formatPlanPriceFromBase(addonTotals.yearly_price, selectedCurrency)}</p>
            </div>
          </div>
          <div>
            <Label>Description</Label>
            <Textarea value={form.description} onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <span className="grid h-8 w-8 place-items-center rounded-lg bg-info/10 text-info">
              <Gauge className="h-4 w-4" />
            </span>
            Limits &amp; Capacity
          </CardTitle>
          <CardDescription>User, employee, storage, and tenant entitlements for this plan.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 md:grid-cols-3">
            <div>
              <Label>Max Users</Label>
              <Input
                type="number"
                value={String(form.usage_limits?.max_users ?? '')}
                onChange={(e) => setForm((prev) => ({ ...prev, usage_limits: { ...prev.usage_limits, max_users: e.target.value } }))}
              />
              <p className="mt-1 text-xs text-muted-foreground">Use `-1` for unlimited users.</p>
            </div>
            <div>
              <Label>Maximum Employees</Label>
              <Input
                type="number"
                value={String(form.usage_limits?.max_employees ?? '')}
                onChange={(e) => setForm((prev) => ({ ...prev, usage_limits: { ...prev.usage_limits, max_employees: e.target.value } }))}
              />
              <p className="mt-1 text-xs text-muted-foreground">Use `-1` for unlimited employees.</p>
            </div>
            <div>
              <Label>Storage Limit (GB)</Label>
              <Input
                type="number"
                min="0"
                step="1"
                value={String(form.usage_limits?.storage_limit_gb ?? '')}
                onChange={(e) => setForm((prev) => ({ ...prev, usage_limits: { ...prev.usage_limits, storage_limit_gb: e.target.value } }))}
              />
            </div>
            <div className="md:col-span-3">
              <Label>Billing Model</Label>
              <Select
                value={form.usage_limits?.billing_model || 'self'}
                onValueChange={(value) => setForm((prev) => ({ ...prev, usage_limits: { ...prev.usage_limits, billing_model: value } }))}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="self">self</SelectItem>
                  <SelectItem value="parent">parent</SelectItem>
                  <SelectItem value="hybrid">hybrid</SelectItem>
                  <SelectItem value="flexible">flexible</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid md:grid-cols-4 gap-3">
            <div>
              <Label>Tenants Limit</Label>
              <Input
                value={String(form.usage_limits?.tenants ?? '')}
                onChange={(e) => setForm((prev) => ({ ...prev, usage_limits: { ...prev.usage_limits, tenants: e.target.value } }))}
              />
            </div>
            <div>
              <Label>Child Tenants</Label>
              <Input
                value={String(form.usage_limits?.sub_tenants ?? '')}
                onChange={(e) => setForm((prev) => ({ ...prev, usage_limits: { ...prev.usage_limits, sub_tenants: e.target.value } }))}
              />
            </div>
            <div>
              <Label>Users / Tenant</Label>
              <Input
                value={String(form.usage_limits?.users_per_tenant ?? '')}
                onChange={(e) => setForm((prev) => ({ ...prev, usage_limits: { ...prev.usage_limits, users_per_tenant: e.target.value } }))}
              />
            </div>
            <div>
              <Label>Other Add-ons (comma)</Label>
              <Input
                value={customAddonValue}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    usage_limits: {
                      ...prev.usage_limits,
                      addons: [
                        ...(prev.usage_limits?.addons || []).filter((addon) => ALL_ADDON_KEYS.includes(addon)),
                        ...e.target.value.split(',').map((v) => v.trim()).filter(Boolean),
                      ],
                    },
                  }))
                }
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <span className="grid h-8 w-8 place-items-center rounded-lg bg-primary/10 text-primary">
              <Layers3 className="h-4 w-4" />
            </span>
            Plan Insights
          </CardTitle>
          <CardDescription>A quick read on coverage, add-ons, and commercial posture.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 md:grid-cols-4">
            <div className="rounded-xl border border-border bg-muted/10 px-4 py-3">
              <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Modules</p>
              <p className="mt-2 text-lg font-semibold text-foreground">{includedModulesCount}</p>
              <p className="text-xs text-muted-foreground">Included modules in this plan</p>
            </div>
            <div className="rounded-xl border border-border bg-muted/10 px-4 py-3">
              <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Role Coverage</p>
              <p className="mt-2 text-lg font-semibold text-foreground">{enabledRoleCount}</p>
              <p className="text-xs text-muted-foreground">Roles with explicit permission coverage</p>
            </div>
            <div className="rounded-xl border border-border bg-muted/10 px-4 py-3">
              <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">AI Add-ons</p>
              <p className="mt-2 text-lg font-semibold text-foreground">{selectedAiAddonCount}</p>
              <p className="text-xs text-muted-foreground">Selected AI Center add-ons update the current plan total</p>
            </div>
            <div className="rounded-xl border border-border bg-muted/10 px-4 py-3">
              <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Commercial Readiness</p>
              <p className="mt-2 text-lg font-semibold text-foreground">{isFreePlan ? 'Startup onboarding' : 'Recurring billing'}</p>
              <p className="text-xs text-muted-foreground">{trialDays > 0 ? `${trialDays}-day trial enabled` : 'No trial access configured'}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <span className="grid h-8 w-8 place-items-center rounded-lg bg-success/10 text-success">
              <Sparkles className="h-4 w-4" />
            </span>
            Features &amp; Trial
          </CardTitle>
          <CardDescription>AI integration and trial availability for this plan.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
              <div className="flex items-center justify-between rounded-md border p-4">
                <div>
                  <Label className="text-sm">AI Integration</Label>
                  <p className="text-xs text-muted-foreground">Enable AI-powered modules and AI Center extensions for this plan.</p>
                </div>
                <Switch
                  checked={isAiIntegrationEnabled}
                  onCheckedChange={(checked) =>
                    setForm((prev) => ({
                      ...prev,
                      usage_limits: {
                        ...prev.usage_limits,
                        ai_integration: Boolean(checked),
                        addons: checked
                          ? prev.usage_limits?.addons || []
                          : (prev.usage_limits?.addons || []).filter((addon) => !aiAddonKeys.includes(addon)),
                      },
                    }))
                  }
                />
              </div>
              <div className="flex items-center justify-between rounded-md border p-4">
                <div>
                  <Label className="text-sm">Enable Trial</Label>
                  <p className="text-xs text-muted-foreground">Allow this plan to run on a trial period before upgrade.</p>
                </div>
                <Switch
                  checked={isTrialEnabled}
                  onCheckedChange={(checked) =>
                    setForm((prev) => ({
                      ...prev,
                      usage_limits: {
                        ...prev.usage_limits,
                        enable_trial: Boolean(checked),
                        trial_days: checked ? (prev.usage_limits?.trial_days || 14) : prev.usage_limits?.trial_days || 0,
                      },
                    }))
                  }
                />
              </div>
            </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <span className="grid h-8 w-8 place-items-center rounded-lg bg-muted text-muted-foreground">
              <Settings2 className="h-4 w-4" />
            </span>
            Settings
          </CardTitle>
          <CardDescription>Availability and default-assignment behavior.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="flex items-center justify-between rounded-md border p-4">
                <div>
                  <Label className="text-sm">Active</Label>
                  <p className="text-xs text-muted-foreground">Control whether this plan is available for assignment.</p>
                </div>
                <Switch
                  checked={Boolean(form.is_active)}
                  onCheckedChange={(checked) => setForm((prev) => ({ ...prev, is_active: Boolean(checked) }))}
                />
              </div>
              <div className="flex items-center justify-between rounded-md border p-4">
                <div>
                  <Label className="text-sm">Default Plan</Label>
                  <p className="text-xs text-muted-foreground">Mark this as the default plan used for fresh tenant subscription assignment.</p>
                </div>
                <Switch
                  checked={isDefaultPlan}
                  onCheckedChange={(checked) =>
                    setForm((prev) => ({
                      ...prev,
                      usage_limits: {
                        ...prev.usage_limits,
                        is_default_plan: Boolean(checked),
                      },
                    }))
                  }
                />
              </div>
            </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <span className="grid h-8 w-8 place-items-center rounded-lg bg-purple/10 text-purple">
              <Sparkles className="h-4 w-4" />
            </span>
            AI Center Add-ons
          </CardTitle>
          <CardDescription>
            Enable premium AI Center modules for the selected plan and define the additional monthly and yearly uplift.
          </CardDescription>
        </CardHeader>
        <CardContent>
            <div className="grid gap-4">
              {AI_DASHBOARD_ADDON_DEFINITIONS.map((addon) => {
                const enabled = Boolean(form.usage_limits?.addons?.includes(addon.key));
                const pricing = getAiDashboardAddonPricing(form.usage_limits, addon.key);

                return (
                  <div key={addon.key} className={`rounded-xl border border-border p-4 transition-colors ${enabled ? 'border-primary/30 bg-primary/5' : ''} ${!isAiIntegrationEnabled ? 'opacity-60' : ''}`}>
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                      <div className="space-y-2">
                        <label className="flex items-center gap-2 text-sm font-medium">
                          <Checkbox
                            disabled={!isAiIntegrationEnabled}
                            checked={enabled}
                            onCheckedChange={(checked) => toggleAddon(addon.key, Boolean(checked))}
                          />
                          <span>{addon.label}</span>
                        </label>
                        <p className="text-sm text-muted-foreground">{addon.description}</p>
                      </div>
                      <div className="grid w-full gap-3 md:grid-cols-2 lg:max-w-md">
                        <div>
                          <Label>Monthly Add-on ({selectedCurrency})</Label>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            disabled={!isAiIntegrationEnabled}
                            value={convertPlanPriceFromBase(pricing.monthly_price, selectedCurrency)}
                            onChange={(event) => updateAddonPricing(addon.key, 'monthly_price', convertPlanPriceToBase(event.target.value, selectedCurrency))}
                          />
                        </div>
                        <div>
                          <Label>Yearly Add-on ({selectedCurrency})</Label>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            disabled={!isAiIntegrationEnabled}
                            value={convertPlanPriceFromBase(pricing.yearly_price, selectedCurrency)}
                            onChange={(event) => updateAddonPricing(addon.key, 'yearly_price', convertPlanPriceToBase(event.target.value, selectedCurrency))}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <span className="grid h-8 w-8 place-items-center rounded-lg bg-purple/10 text-purple">
              <Sparkles className="h-4 w-4" />
            </span>
            Education Suite Add-ons
          </CardTitle>
          <CardDescription>
            Sell Education modules à la carte for tenants on this plan. Enable only the modules you want (faculty, calendar, timetable, accreditation, research, grievance and more) and set each one's monthly / yearly uplift.
          </CardDescription>
        </CardHeader>
        <CardContent>
            <div className="grid gap-4">
              {SUITE_ADDON_DEFINITIONS.map((addon) => {
                const enabled = Boolean(form.usage_limits?.addons?.includes(addon.key));
                const pricing = getAiDashboardAddonPricing(form.usage_limits, addon.key);
                return (
                  <div key={addon.key} className={`rounded-xl border border-border p-4 transition-colors ${enabled ? 'border-primary/30 bg-primary/5' : ''}`}>
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                      <div className="space-y-2">
                        <label className="flex items-center gap-2 text-sm font-medium">
                          <Checkbox
                            checked={enabled}
                            onCheckedChange={(checked) => toggleAddon(addon.key, Boolean(checked))}
                          />
                          <span>{addon.label}</span>
                        </label>
                        <p className="text-sm text-muted-foreground">{addon.description}</p>
                      </div>
                      <div className="grid w-full gap-3 md:grid-cols-2 lg:max-w-md">
                        <div>
                          <Label>Monthly Add-on ({selectedCurrency})</Label>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={convertPlanPriceFromBase(pricing.monthly_price, selectedCurrency)}
                            onChange={(event) => updateAddonPricing(addon.key, 'monthly_price', convertPlanPriceToBase(event.target.value, selectedCurrency))}
                          />
                        </div>
                        <div>
                          <Label>Yearly Add-on ({selectedCurrency})</Label>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={convertPlanPriceFromBase(pricing.yearly_price, selectedCurrency)}
                            onChange={(event) => updateAddonPricing(addon.key, 'yearly_price', convertPlanPriceToBase(event.target.value, selectedCurrency))}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <span className="grid h-8 w-8 place-items-center rounded-lg bg-info/10 text-info">
              <ShieldCheck className="h-4 w-4" />
            </span>
            Access Control
          </CardTitle>
          <CardDescription>Module entitlements and per-role permission coverage.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="border border-border rounded-xl p-4">
              <p className="font-medium mb-2 text-foreground">Module Access</p>
              <div className="grid md:grid-cols-2 gap-2">
                {catalog.modules.map((moduleKey) => (
                  <label key={moduleKey} className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={form.module_access.includes(moduleKey)}
                      onCheckedChange={(checked) => toggleModule(moduleKey, Boolean(checked))}
                    />
                    <span>{moduleKey}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="border border-border rounded-xl p-4">
              <p className="font-medium mb-2 text-foreground">Role Permissions</p>
              <div className="space-y-3 max-h-[340px] overflow-y-auto pr-1">
                {catalog.roles.map((role) => (
                  <div key={role}>
                    <p className="text-sm font-semibold mb-1 text-foreground">{role}</p>
                    <div className="grid grid-cols-2 gap-1">
                      {catalog.permissions.map((permission) => (
                        <label key={`${role}-${permission}`} className="flex items-center gap-2 text-xs">
                          <Checkbox
                            checked={Boolean(form.role_permissions?.[role]?.includes(permission))}
                            onCheckedChange={(checked) => togglePermission(role, permission, Boolean(checked))}
                          />
                          <span>{permission}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="sticky bottom-4 z-10 flex items-center gap-2 rounded-xl border border-border bg-card/95 p-3 shadow-lg backdrop-blur supports-[backdrop-filter]:bg-card/80">
        <Button onClick={savePlan} disabled={saving}>
          <Check className="h-4 w-4 mr-2" />
          {saving ? 'Saving...' : isCreateMode ? 'Create Plan' : 'Save Changes'}
        </Button>
        <Button variant="outline" onClick={onBack}>Cancel</Button>
      </div>
    </div>
  );
};

export default SuperadminPlanEditPage;
